/**
 * PR-34A — Anti-Fraud Foundation: server-authoritative RPCs.
 *
 * The client never decides XP, never decides "referral completed".
 * It asks; the server validates, caps, audits and grants.
 */
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

export interface GrantXpResult {
  ok: boolean;
  granted?: number;
  reason?: string;
  grant_id?: string;
  idempotency_key?: string;
}

export const grantXpSecure = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z
      .object({
        reason: z.string().min(2).max(64),
        amount: z.number().int().min(0).max(1000),
        idempotencyKey: z.string().min(6).max(128),
        metadata: z.record(z.string(), z.unknown()).optional(),
        deviceHash: z.string().max(128).optional(),
      })
      .parse(input),
  )
  .handler(async ({ data, context }): Promise<GrantXpResult> => {
    const { supabase } = context;
    const { data: out, error } = await supabase.rpc("grant_xp_secure", {
      _reason: data.reason,
      _amount: data.amount,
      _idempotency_key: data.idempotencyKey,
      _metadata: (data.metadata ?? {}) as never,
      _device_hash: data.deviceHash ?? undefined,
    });
    if (error) return { ok: false, reason: error.message };
    return (out as unknown as GrantXpResult) ?? { ok: false, reason: "no_result" };
  });

export interface AttachReferralResult {
  ok: boolean;
  status?: string;
  reason?: string;
}

export const attachPendingReferral = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z
      .object({
        code: z.string().min(4).max(64),
        ipHash: z.string().max(128).optional(),
        uaHash: z.string().max(128).optional(),
        deviceHash: z.string().max(128).optional(),
      })
      .parse(input),
  )
  .handler(async ({ data, context }): Promise<AttachReferralResult> => {
    const { supabase } = context;
    const { data: out, error } = await supabase.rpc("attach_pending_referral", {
      _code: data.code,
      _ip_hash: data.ipHash ?? undefined,
      _ua_hash: data.uaHash ?? undefined,
      _device_hash: data.deviceHash ?? undefined,
    });
    if (error) return { ok: false, reason: error.message };
    return (out as unknown as AttachReferralResult) ?? { ok: false, reason: "no_result" };
  });

export interface FinalizeReferralResult {
  ok: boolean;
  status?: string;
  reason?: string;
  referrer_user_id?: string | null;
}

export const finalizeReferralAfterFirstPrediction = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<FinalizeReferralResult> => {
    const { supabase } = context;
    const { data: out, error } = await supabase.rpc(
      "finalize_referral_after_first_prediction",
    );
    if (error) return { ok: false, reason: error.message };
    return (out as unknown as FinalizeReferralResult) ?? { ok: false, reason: "no_result" };
  });

/**
 * Verify a Cloudflare Turnstile token. Soft-fails OPEN (ok:true, skipped:true)
 * when CF_TURNSTILE_SECRET is not configured — keeps existing UX intact
 * while allowing immediate enforcement the moment the secret is added.
 */
export interface TurnstileResult {
  ok: boolean;
  skipped?: boolean;
  reason?: string;
}

export const verifyTurnstile = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) =>
    z.object({ token: z.string().min(1).max(4096) }).parse(input),
  )
  .handler(async ({ data }): Promise<TurnstileResult> => {
    const secret = process.env.CF_TURNSTILE_SECRET;
    if (!secret) return { ok: true, skipped: true };
    try {
      const body = new URLSearchParams();
      body.set("secret", secret);
      body.set("response", data.token);
      const res = await fetch(
        "https://challenges.cloudflare.com/turnstile/v0/siteverify",
        { method: "POST", body },
      );
      const json = (await res.json()) as { success?: boolean; "error-codes"?: string[] };
      return json.success
        ? { ok: true }
        : { ok: false, reason: (json["error-codes"] ?? ["unknown"]).join(",") };
    } catch (e) {
      return { ok: false, reason: e instanceof Error ? e.message : "turnstile_error" };
    }
  });

/* ----------------------------------------------------------------- */
/* PR-34B — Device fingerprint + risk-scoring server functions       */
/* ----------------------------------------------------------------- */

export type DeviceSignalKind =
  | "guest_prediction"
  | "signup_completed"
  | "xp_claim"
  | "referral_attached"
  | "login"
  | "oauth_callback"
  | "guest_claim_attempt";

export interface DeviceRisk {
  score: number;
  band: "LOW" | "MEDIUM" | "HIGH";
  flags: string[];
  users?: number;
  signups_24h?: number;
  claims_24h?: number;
  referrals_7d?: number;
}

export interface RecordSignalResult {
  ok: boolean;
  risk?: DeviceRisk;
  reason?: string;
}

export const recordDeviceSignal = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) =>
    z
      .object({
        deviceHash: z.string().min(8).max(128),
        kind: z.enum([
          "guest_prediction",
          "signup_completed",
          "xp_claim",
          "referral_attached",
          "login",
          "oauth_callback",
          "guest_claim_attempt",
        ]),
        ipHash: z.string().max(128).optional(),
        sessionId: z.string().max(128).optional(),
        metadata: z.record(z.string(), z.unknown()).optional(),
      })
      .parse(input),
  )
  .handler(async ({ data }): Promise<RecordSignalResult> => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: out, error } = await supabaseAdmin.rpc("record_device_signal", {
      _device_hash: data.deviceHash,
      _kind: data.kind,
      _ip_hash: data.ipHash ?? undefined,
      _session_id: data.sessionId ?? undefined,
      _metadata: (data.metadata ?? {}) as never,
    });
    if (error) return { ok: false, reason: error.message };
    return (out as unknown as RecordSignalResult) ?? { ok: false, reason: "no_result" };
  });

export interface ClaimGuestXpResult {
  ok: boolean;
  granted?: number;
  reason?: string;
  grant_id?: string;
}

export const claimGuestPredictionXp = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z
      .object({
        deviceHash: z.string().min(8).max(128),
        idempotencyKey: z.string().min(6).max(128),
      })
      .parse(input),
  )
  .handler(async ({ data, context }): Promise<ClaimGuestXpResult> => {
    const { supabase } = context;
    const { data: out, error } = await supabase.rpc("claim_guest_prediction_xp", {
      _device_hash: data.deviceHash,
      _idempotency_key: data.idempotencyKey,
    });
    if (error) return { ok: false, reason: error.message };
    return (out as unknown as ClaimGuestXpResult) ?? { ok: false, reason: "no_result" };
  });

export const computeDeviceRisk = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) =>
    z.object({ deviceHash: z.string().min(8).max(128) }).parse(input),
  )
  .handler(async ({ data }): Promise<DeviceRisk | null> => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: out, error } = await supabaseAdmin.rpc("compute_device_risk", {
      _device_hash: data.deviceHash,
    });
    if (error) return null;
    return (out as unknown as DeviceRisk) ?? null;
  });

export interface SuspiciousOverview {
  ok: boolean;
  error?: string;
  payload?: string; // JSON-encoded SuspiciousOverviewPayload
}

export interface SuspiciousOverviewPayload {
  suspicious_devices: Array<Record<string, unknown>>;
  suspicious_referrals: Array<Record<string, unknown>>;
  suspicious_xp_claims: Array<Record<string, unknown>>;
  recent_audit: Array<Record<string, unknown>>;
  generated_at: string;
}

export const adminSuspiciousOverview = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<SuspiciousOverview> => {
    const { supabase } = context;
    const { data, error } = await supabase.rpc("admin_suspicious_overview");
    if (error) return { ok: false, error: error.message };
    return { ok: true, payload: JSON.stringify(data ?? {}) };
  });